import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '../../../../server/src/routers/index';
import CourseComments from './CourseComments';
import IntroVideo from './IntroVideo';

type Course = inferRouterOutputs<AppRouter>['courses']['list'][number];

interface Props {
  course:    Course;
  purchased: boolean;
  onPurchase:(id: number) => void;
  onClose:   () => void;
}

export default function CourseModal({ course, purchased, onPurchase, onClose }: Props) {
  const totalMins = course.sections.reduce((a, s) => a + s.durationMinutes, 0);

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50
                 flex items-center justify-center p-4 md:p-6"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="mp-card w-full max-w-lg max-h-[90vh] overflow-y-auto scrollbar-hide
                      shadow-[0_16px_64px_rgba(0,0,0,0.7)]">
        {/* Cover image (falls back to yellow band) */}
        {(course as any).imageUrl ? (
          <div className="w-full h-44 rounded-t-3xl overflow-hidden relative">
            <img
              src={(course as any).imageUrl}
              alt={course.title}
              className="w-full h-full object-cover"
            />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 bg-black/60 hover:bg-black/80
                         rounded-full flex items-center justify-center text-white
                         text-sm transition-colors"
            >
              ✕
            </button>
          </div>
        ) : null}

        <div className="p-6">
          {/* Close (only when no cover image) */}
          {!(course as any).imageUrl && (
            <div className="flex justify-end mb-4">
              <button
                onClick={onClose}
                className="w-8 h-8 bg-[#27272A] hover:bg-[#3F3F46] rounded-full
                           flex items-center justify-center text-[#A1A1AA]
                           hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          )}

          <h2 className="text-3xl font-bold text-white mb-1 leading-tight">
            {course.title}
          </h2>
          {(course as any).instructor?.name && (
            <p className="text-yellow-400 text-sm font-semibold mb-1">
              with {(course as any).instructor.name}
            </p>
          )}
          <p className="text-[#71717A] text-lg mb-2">
            {course.sections.length} modules · {Math.floor(totalMins / 60)}h {totalMins % 60}m
          </p>
          {course.description && (
            <p className="text-[#E4E4E7] text-lg leading-relaxed mb-4">{course.description}</p>
          )}

          {/* Free preview video — visible to everyone, renders nothing if unset */}
          <div className="mb-4">
            <IntroVideo courseId={course.id} />
          </div>

          <div className="mp-divider my-4" />

          <h3 className="text-white font-semibold text-sm uppercase tracking-wide mb-3">
            Course Content
          </h3>
          <div className="space-y-2 mb-6">
            {course.sections
              .slice().sort((a, b) => a.order - b.order)
              .map(s => (
                <div key={s.id}
                     className="flex items-center justify-between bg-black
                                rounded-2xl px-4 py-3 border border-[#27272A]">
                  <span className="text-[#E4E4E7] text-sm">
                    <span className="text-yellow-400 mr-2">▶</span>{s.name}
                  </span>
                  <span className="text-yellow-400 text-xs font-semibold shrink-0 ml-3">
                    {s.durationMinutes} min
                  </span>
                </div>
              ))}
          </div>

          <div className="mp-divider mb-4" />

          {!purchased && (
            <div className="flex items-center justify-between mb-5">
              <span className="text-[#A1A1AA] text-lg">Price</span>
              <span className="text-2xl font-bold text-yellow-400">
                {parseFloat(course.price as string) === 0 ? 'Free' : `${course.price} KD`}
              </span>
            </div>
          )}

          {purchased ? (
            <a
              href={`/courses/${course.id}/watch`}
              className="block w-full text-center py-4 bg-green-500/10 text-green-400
                         border border-green-500/20 rounded-2xl font-semibold text-sm
                         hover:bg-green-500/20 transition-colors"
            >
              ✓ Purchased — Watch now
            </a>
          ) : (
            <>
              <button
                onClick={() => { onPurchase(course.id); onClose(); }}
                className="mp-btn mp-btn-primary w-full py-4 rounded-2xl text-base"
              >
                Enroll — {parseFloat(course.price as string) === 0 ? 'Free' : `${course.price} KD`}
              </button>
              <p className="text-[#52525B] text-[11px] text-center mt-2">
                Access is for 1 year from purchase.
              </p>
            </>
          )}

          {/* Reviews — visible to everyone; only buyers can post */}
          <CourseComments courseId={course.id} canComment={purchased} />
        </div>
      </div>
    </div>
  );
}
